import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronLeft, ChevronRight, Plus, Tag, Workflow, X } from 'lucide-react'
import Button from '../forms/Button'
import Input from '../forms/Input'
import RuleEditorRow from './RuleEditorRow'
import { createEmptyRule } from '../../data/rulesEngineDefaults'
import { createEmptyBenefitRule } from '../../services/benefitRuleMastersService'
import { useBenefitRuleMasters } from '../../hooks/useBenefitRuleMasters'
import {
  createRule,
  createRuleSet,
  deleteRule as deleteRuleApi,
  getRuleSet,
  updateRule as updateRuleApi,
  updateRuleSet,
} from '../../services/rulesEngineService'
import { useToast } from '../../contexts/ToastContext'
import { useTheme } from '../../contexts/ThemeContext'
import type { BenefitRule, RuleSetRecord } from '../../types/rulesEngine'

type StepId = 'details' | 'rules'

const STEPS: { id: StepId; label: string }[] = [
  { id: 'details', label: 'Details' },
  { id: 'rules', label: 'Rules' },
]

type RuleSetDrawerProps = {
  open: boolean
  mode: 'create' | 'edit'
  ruleSet: RuleSetRecord | null
  onClose: () => void
  onSaved: (record: RuleSetRecord) => void
}

type Draft = {
  name: string
  productServiceSlug: string
  rules: BenefitRule[]
}

function draftFromRuleSet(ruleSet: RuleSetRecord | null): Draft {
  return {
    name: ruleSet?.name ?? '',
    productServiceSlug: ruleSet?.productServiceSlug ?? '',
    rules: ruleSet ? JSON.parse(JSON.stringify(ruleSet.rules)) : [],
  }
}

export default function RuleSetDrawer({
  open,
  mode,
  ruleSet,
  onClose,
  onSaved,
}: RuleSetDrawerProps) {
  const { theme } = useTheme()
  const { showToast } = useToast()
  const {
    fields: benefitFields,
    operators: benefitOperators,
    actions: benefitActions,
    fieldValuesByFieldId,
    loadFieldValues,
    loading: mastersLoading,
    hasMasters,
  } = useBenefitRuleMasters(open)

  const [activeStep, setActiveStep] = useState<StepId>('details')
  const [draft, setDraft] = useState<Draft>(() => draftFromRuleSet(ruleSet))
  const [recordId, setRecordId] = useState<string | null>(ruleSet?.id ?? null)
  const [originalRuleIds, setOriginalRuleIds] = useState<Set<string>>(
    () => new Set((ruleSet?.rules ?? []).map((r) => r.id).filter(Boolean))
  )
  const [originalRulesById, setOriginalRulesById] = useState<Record<string, BenefitRule>>({})
  const [nameError, setNameError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    const rules = ruleSet?.rules ?? []
    const byId: Record<string, BenefitRule> = {}
    for (const rule of rules) {
      if (rule.id) byId[rule.id] = JSON.parse(JSON.stringify(rule)) as BenefitRule
    }
    setDraft(draftFromRuleSet(ruleSet))
    setRecordId(ruleSet?.id ?? null)
    setOriginalRuleIds(new Set(Object.keys(byId)))
    setOriginalRulesById(byId)
    setActiveStep('details')
    setNameError(null)
  }, [open, ruleSet])

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  const activeStepIndex = useMemo(
    () => STEPS.findIndex((s) => s.id === activeStep),
    [activeStep]
  )
  const isFirstStep = activeStepIndex <= 0
  const isLastStep = activeStepIndex === STEPS.length - 1

  const close = () => {
    setActiveStep('details')
    setNameError(null)
    onClose()
  }

  const updateDraftRule = (idx: number, rule: BenefitRule) => {
    setDraft((prev) => {
      const next = [...prev.rules]
      next[idx] = rule
      return { ...prev, rules: next }
    })
  }

  const deleteRule = (idx: number) => {
    setDraft((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }))
  }

  const moveRule = (idx: number, dir: -1 | 1) => {
    setDraft((prev) => {
      const next = [...prev.rules]
      const target = idx + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return { ...prev, rules: next }
    })
  }

  const addRule = () => {
    const nextRule =
      hasMasters && benefitFields.length > 0
        ? createEmptyBenefitRule(benefitFields, benefitOperators, benefitActions)
        : createEmptyRule()
    setDraft((prev) => ({ ...prev, rules: [...prev.rules, nextRule] }))
  }

  const handleSaveAndProceed = () => {
    if (!draft.name.trim()) {
      setNameError('Rule set name is required.')
      showToast('Rule set name is required.', { type: 'error' })
      return
    }
    setNameError(null)
    setActiveStep('rules')
  }

  const persistCreate = async (): Promise<RuleSetRecord> => {
    const created = await createRuleSet({
      name: draft.name.trim(),
      productServiceSlug: draft.productServiceSlug.trim() || undefined,
      status: 'active',
      rules: draft.rules,
    })
    setRecordId(created.id)
    return created
  }

  const persistEdit = async (): Promise<RuleSetRecord> => {
    if (!recordId) throw new Error('Rule set id is missing.')

    const remainingIds = new Set(draft.rules.map((rule) => rule.id).filter(Boolean))
    for (const ruleId of originalRuleIds) {
      if (!remainingIds.has(ruleId)) {
        await deleteRuleApi(recordId, ruleId)
      }
    }

    const updatedRules: BenefitRule[] = []
    for (const rule of draft.rules) {
      const isExisting = Boolean(rule.id) && originalRuleIds.has(rule.id)

      if (isExisting) {
        const original = originalRulesById[rule.id]
        const nextName = rule.name.trim() || 'New Rule'
        const conditionChanged =
          original?.field !== rule.field ||
          original?.operator !== rule.operator ||
          original?.value !== rule.value ||
          original?.action !== rule.action ||
          original?.failAction !== rule.failAction ||
          original?.failMessage !== rule.failMessage ||
          original?.conditionFieldMasterId !== rule.conditionFieldMasterId ||
          original?.conditionOperatorMasterId !== rule.conditionOperatorMasterId ||
          original?.conditionValueMasterId !== rule.conditionValueMasterId ||
          original?.onPassActionMasterId !== rule.onPassActionMasterId ||
          original?.onFailActionMasterId !== rule.onFailActionMasterId ||
          (original?.name ?? '') !== nextName

        if (!conditionChanged) {
          updatedRules.push(rule)
          continue
        }

        const saved = await updateRuleApi(recordId, rule.id, rule)
        updatedRules.push({
          ...rule,
          ...saved,
          id: rule.id,
          field: rule.field || saved.field,
          operator: rule.operator || saved.operator,
          action: rule.action || saved.action,
          failAction: rule.failAction || saved.failAction,
          failMessage: rule.failMessage || saved.failMessage,
          value: rule.value || saved.value,
          name: saved.name || nextName,
          conditionFieldMasterId: rule.conditionFieldMasterId ?? saved.conditionFieldMasterId,
          conditionOperatorMasterId:
            rule.conditionOperatorMasterId ?? saved.conditionOperatorMasterId,
          conditionValueMasterId: rule.conditionValueMasterId ?? saved.conditionValueMasterId,
          onPassActionMasterId: rule.onPassActionMasterId ?? saved.onPassActionMasterId,
          onFailActionMasterId: rule.onFailActionMasterId ?? saved.onFailActionMasterId,
        })
        continue
      }

      const created = await createRule(recordId, rule)
      updatedRules.push({
        ...rule,
        ...created,
        id: created.id || rule.id,
        field: created.field || rule.field,
        operator: created.operator || rule.operator,
        action: created.action || rule.action,
        failAction: created.failAction || rule.failAction,
        failMessage: created.failMessage || rule.failMessage,
        value: created.value || rule.value,
        name: created.name || rule.name,
      })
    }

    await updateRuleSet(recordId, {
      name: draft.name.trim(),
      productServiceSlug: draft.productServiceSlug.trim() || undefined,
      rules: updatedRules,
      isActive: ruleSet?.isActive ?? true,
      status: ruleSet?.status ?? (ruleSet?.isActive === false ? 'inactive' : 'active'),
    })

    const refreshed = await getRuleSet(recordId)
    if (refreshed) {
      return {
        ...refreshed,
        name: draft.name.trim() || refreshed.name,
        productServiceSlug: draft.productServiceSlug.trim() || refreshed.productServiceSlug,
        rules: refreshed.rules.length > 0 ? refreshed.rules : updatedRules,
      }
    }

    return {
      id: recordId,
      name: draft.name.trim(),
      productServiceSlug: draft.productServiceSlug.trim() || undefined,
      rules: updatedRules,
      isActive: ruleSet?.isActive ?? true,
      status: ruleSet?.status ?? 'active',
      createdAt: ruleSet?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  }

  const handleFinish = async () => {
    if (!draft.name.trim()) {
      setNameError('Rule set name is required.')
      setActiveStep('details')
      showToast('Rule set name is required.', { type: 'error' })
      return
    }
    setIsSaving(true)
    try {
      const finalRecord = mode === 'create' ? await persistCreate() : await persistEdit()
      onSaved(finalRecord)
      showToast(mode === 'edit' ? 'Rule set updated.' : 'Rule set created.', { type: 'success' })
      close()
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save rule set.', {
        type: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const goToPreviousStep = () => {
    if (isFirstStep) return
    setActiveStep(STEPS[activeStepIndex - 1].id)
  }

  const goToStep = (id: StepId) => {
    if (id === 'rules' && !draft.name.trim()) {
      setNameError('Rule set name is required.')
      showToast('Rule set name is required.', { type: 'error' })
      return
    }
    setActiveStep(id)
  }

  const title = mode === 'edit' ? 'Edit rule set' : 'Add rule set'
  const subtitle =
    mode === 'edit'
      ? draft.name || 'Update rule set details and rules'
      : 'Define rule set details, then add the rules'

  if (!open) return null

  return createPortal(
    <>
      <div
        className="fixed inset-0 top-0 z-[200] bg-slate-900/35 transition-opacity"
        onClick={close}
        aria-hidden
      />
      <div
        className="fixed bottom-0 right-0 top-0 z-[201] flex w-full flex-col border-l shadow-[-8px_0_32px_rgba(15,23,42,0.1)] transition-transform duration-300 sm:w-3/4 lg:w-2/3"
        style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div
          className="flex shrink-0 items-center justify-between border-b px-6 py-5 sm:px-8"
          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.cardBackground }}
        >
          <div className="flex min-w-0 items-center gap-3">
            <div
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
              style={{ backgroundColor: theme.colors.buttonPrimary, color: '#fff' }}
            >
              <Workflow className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
                {title}
              </h2>
              <p className="mt-0.5 truncate text-sm" style={{ color: theme.colors.textSecondary }}>
                {subtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors"
            style={{ borderColor: theme.colors.border, color: theme.colors.textSecondary }}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Step indicator */}
        <div
          className="shrink-0 border-b px-6 py-4 sm:px-8"
          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.cardBackground }}
        >
          <div className="flex items-center gap-3">
            {STEPS.map((step, index) => {
              const isCompleted = index < activeStepIndex
              const isCurrent = step.id === activeStep
              const canClick =
                mode === 'edit' ||
                index <= activeStepIndex ||
                (step.id === 'rules' && Boolean(draft.name.trim()))
              return (
                <div key={step.id} className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => canClick && goToStep(step.id)}
                    disabled={!canClick}
                    className="inline-flex items-center gap-2 rounded-full transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-current={isCurrent ? 'step' : undefined}
                  >
                    <span
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold"
                      style={{
                        backgroundColor: isCompleted
                          ? '#16a34a'
                          : isCurrent
                            ? theme.colors.buttonPrimary
                            : theme.colors.border,
                        color: isCompleted || isCurrent ? '#fff' : theme.colors.textSecondary,
                      }}
                    >
                      {isCompleted ? <Check size={12} /> : index + 1}
                    </span>
                    <span
                      className="whitespace-nowrap text-xs font-medium sm:text-sm"
                      style={{ color: isCurrent ? theme.colors.textPrimary : theme.colors.textSecondary }}
                    >
                      {step.label}
                    </span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <span className="h-px w-6 sm:w-8" style={{ backgroundColor: theme.colors.border }} />
                  )}
                </div>
              )
            })}
          </div>
          <p className="mt-3 text-xs" style={{ color: theme.colors.textMuted }}>
            Step {activeStepIndex + 1} of {STEPS.length}
          </p>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8">
          {activeStep === 'details' ? (
            <div className="space-y-5">
              <div
                className="flex items-start gap-3 rounded-xl border p-4"
                style={{ borderColor: theme.colors.cardBorder, backgroundColor: theme.colors.cardBackground }}
              >
                <div
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-lg"
                  style={{ backgroundColor: theme.colors.buttonPrimary, color: '#fff' }}
                >
                  <Workflow className="h-4 w-4" />
                </div>
                <p className="text-xs" style={{ color: theme.colors.textSecondary }}>
                  Name the rule set for a product category. Add the individual rules in the next step.
                </p>
              </div>

              <Input
                name="rule-set-name"
                label="Rule set name"
                required
                value={draft.name}
                onChange={(e) => {
                  setDraft({ ...draft, name: e.target.value })
                  if (nameError) setNameError(null)
                }}
                placeholder="e.g. CPAP, DME General, Orthotics"
                error={nameError ?? undefined}
              />

              <div>
                <Input
                  name="rule-set-slug"
                  label="Product service slug (optional)"
                  value={draft.productServiceSlug}
                  onChange={(e) => setDraft({ ...draft, productServiceSlug: e.target.value })}
                  placeholder="e.g. cpap, orthotics"
                />
                <p className="mt-1 flex items-center gap-1 text-[11px]" style={{ color: theme.colors.textMuted }}>
                  <Tag className="h-3 w-3" /> Used to auto-match this rule set to eligibility results.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold" style={{ color: theme.colors.textPrimary }}>
                    Rules ({draft.rules.length})
                  </h3>
                  <p className="text-xs" style={{ color: theme.colors.textMuted }}>
                    Evaluated top-to-bottom · first terminal action wins.
                    {mastersLoading ? ' · Loading masters…' : ''}
                  </p>
                </div>
                <Button type="button" variant="primary" size="sm" onClick={addRule}>
                  <span className="inline-flex items-center gap-1.5">
                    <Plus className="h-3.5 w-3.5" />
                    Add Rule
                  </span>
                </Button>
              </div>

              {draft.rules.length === 0 ? (
                <div
                  className="rounded-2xl border border-dashed py-10 text-center"
                  style={{ borderColor: theme.colors.border }}
                >
                  <Workflow className="mx-auto mb-2 h-8 w-8" style={{ color: theme.colors.textMuted }} />
                  <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                    No rules yet
                  </p>
                  <p className="text-xs" style={{ color: theme.colors.textMuted }}>
                    Add your first rule to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {draft.rules.map((rule, i) => (
                    <RuleEditorRow
                      key={rule.id}
                      rule={rule}
                      index={i}
                      totalRules={draft.rules.length}
                      fields={benefitFields}
                      operators={benefitOperators}
                      actions={benefitActions}
                      fieldValuesByFieldId={fieldValuesByFieldId}
                      onLoadFieldValues={loadFieldValues}
                      useApiMasters={hasMasters}
                      onUpdate={(updated) => updateDraftRule(i, updated)}
                      onDelete={() => deleteRule(i)}
                      onMove={moveRule}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex shrink-0 flex-col-reverse gap-3 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8"
          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.cardBackground }}
        >
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={close} disabled={isSaving}>
              Cancel
            </Button>
            {!isFirstStep && (
              <Button
                type="button"
                variant="secondary"
                onClick={goToPreviousStep}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5"
              >
                <ChevronLeft size={16} />
                Previous
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3 sm:justify-end">
            {!isLastStep ? (
              <Button
                type="button"
                variant="primary"
                onClick={handleSaveAndProceed}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5"
              >
                Continue
                <ChevronRight size={16} />
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                onClick={() => void handleFinish()}
                disabled={isSaving}
              >
                {isSaving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create rule set'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
